import { useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Phone, Instagram, MapPin, Clock, ArrowUpRight, CalendarDays } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { RevealHeading, Reveal } from "../components/site/Reveal";
import { Medallion } from "../components/site/Ornaments";
import { BUSINESS, SERVICES, whatsappLink } from "../data/site";
import { submitEnquiry } from "../lib/api";

const initial = { name: "", phone: "", email: "", service: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await submitEnquiry({
        ...form,
        event_date: date ? format(date, "yyyy-MM-dd") : "",
      });
      toast.success("Enquiry sent! Arjun will reach out shortly.");
      setForm(initial);
      setDate(null);
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32">
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
        <div className="flex items-center gap-3 text-henna">
          <Medallion size={24} />
          <span className="text-xs uppercase tracking-[0.3em]">
            Bookings &amp; enquiries
          </span>
        </div>
        <RevealHeading
          testId="contact-title"
          className="mt-6 font-display text-[14vw] leading-[0.86] tracking-tight sm:text-[10vw] lg:text-[7.5vw]"
          lines={[<>Let's create</>, <span className="italic font-serif text-henna">something</span>]}
        />
      </section>

      <section className="mx-auto max-w-[1400px] px-5 py-16 sm:px-8 lg:px-12">
        <div className="grid gap-16 lg:grid-cols-12">
          {/* Form */}
          <div className="lg:col-span-7">
            <form onSubmit={onSubmit} data-testid="enquiry-form" className="space-y-10">
              <div className="grid gap-8 sm:grid-cols-2">
                <input
                  className="editorial-input"
                  placeholder="Your name *"
                  value={form.name}
                  onChange={set("name")}
                  data-testid="input-name"
                />
                <input
                  className="editorial-input"
                  placeholder="Phone number *"
                  value={form.phone}
                  onChange={set("phone")}
                  data-testid="input-phone"
                />
              </div>

              <input
                className="editorial-input"
                placeholder="Email (optional)"
                value={form.email}
                onChange={set("email")}
                data-testid="input-email"
              />

              <div className="grid gap-8 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-xs uppercase tracking-[0.18em] text-ink/40">
                    Service
                  </label>
                  <Select
                    value={form.service}
                    onValueChange={(v) => setForm({ ...form, service: v })}
                  >
                    <SelectTrigger
                      data-testid="select-service"
                      className="rounded-none border-0 border-b border-ink/25 bg-transparent px-0 focus:ring-0"
                    >
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((s) => (
                        <SelectItem key={s.id} value={s.title} data-testid={`service-opt-${s.id}`}>
                          {s.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="mb-2 block text-xs uppercase tracking-[0.18em] text-ink/40">
                    Event date
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="date-trigger"
                        className="flex w-full items-center justify-between border-b border-ink/25 py-3 text-left text-sm text-ink/80"
                      >
                        {date ? format(date, "PPP") : "Pick a date"}
                        <CalendarDays className="h-4 w-4 text-ink/40" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <textarea
                className="editorial-input resize-none"
                rows={3}
                placeholder="Tell us about your event..."
                value={form.message}
                onChange={set("message")}
                data-testid="input-message"
              />

              <button
                type="submit"
                disabled={loading}
                data-testid="submit-enquiry"
                className="group inline-flex items-center gap-2 rounded-full bg-ink px-9 py-4 text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-henna disabled:opacity-60"
              >
                {loading ? "Sending..." : "Send enquiry"}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </form>
          </div>

          {/* Details */}
          <div className="lg:col-span-5">
            <div className="border-t border-ink/15 pt-8">
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] py-4 text-xs uppercase tracking-[0.2em] text-white transition-transform duration-300 hover:scale-[1.02]"
              >
                Chat on WhatsApp
              </a>

              <div className="mt-10 space-y-8">
                <a
                  href={`tel:+${BUSINESS.phone}`}
                  className="flex items-start gap-4"
                  data-testid="contact-phone"
                >
                  <Phone className="mt-1 h-5 w-5 text-henna" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-ink/40">
                      Call
                    </p>
                    <p className="text-lg">{BUSINESS.phoneDisplay}</p>
                  </div>
                </a>
                <a
                  href={BUSINESS.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4"
                  data-testid="contact-instagram"
                >
                  <Instagram className="mt-1 h-5 w-5 text-henna" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-ink/40">
                      Instagram
                    </p>
                    <p className="text-lg">{BUSINESS.instagramHandle}</p>
                  </div>
                </a>
                <div className="flex items-start gap-4">
                  <MapPin className="mt-1 h-5 w-5 text-henna" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-ink/40">
                      Studio
                    </p>
                    <p className="text-lg leading-snug">{BUSINESS.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Clock className="mt-1 h-5 w-5 text-henna" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-ink/40">
                      Hours
                    </p>
                    <p className="text-lg">{BUSINESS.hours}</p>
                  </div>
                </div>
              </div>

              <div className="mt-10 overflow-hidden border border-ink/10">
                <iframe
                  title="Arjun Mehndi Arts location"
                  data-testid="contact-map"
                  className="h-64 w-full grayscale"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://maps.google.com/maps?q=${BUSINESS.mapQuery}&output=embed`}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
